import { FC, useContext, useEffect, useMemo } from 'react';
import { StudyApiId } from '@/model/Study.model';
import type { ColorDetail } from '@/constants/colors';
import { GanttViewContext } from '@/components/Tasks/Calendar/Views/Day/GanttView.provider';
import { useModalAction } from '@/utils/modal';
import { packTasks, TaskPosition } from '@/components/Tasks/Calendar/Views/Day/GanttView.utils';
import { useResolvedValue } from '@/support/Hooks/api/useResolvedValue';
import styles from '@/components/Tasks/Calendar/Views/Day/GanttView.module.scss';
import { TaskCard } from '@/components/Tasks/Calendar/Views/Day/TaskCard';
import { _notNil } from '@/littledash';
import cn from 'classnames';
import { TaskApiId, TaskV1 } from '@/model/Task.model';
import { RouteService } from '@/support/RouteService';
import { useNavigate } from 'react-router-dom';
import { DateRenderFormat, DateUtils } from '@/utils/Date.utils';

export const StudyRow: FC<{ studyApiId: StudyApiId; color?: ColorDetail }> = ({ studyApiId, color }) => {
  const context = useContext(GanttViewContext);
  const { openModal } = useModalAction();
  const navigate = useNavigate();
  const openWorkflow = (studyId: number, taskApiId: TaskApiId) => {
    navigate(
      RouteService.web({
        route: 'studies.workflow',
        path: { id: studyId },
        query: { type: 'task-execution', taskApiId: [taskApiId] },
      }).route
    );
  };

  const resolvedStudyInfo = useResolvedValue(() => context.studyInfoCache.get(studyApiId));
  const resolvedTasks = useResolvedValue((props?: { force: boolean }) =>
    context.tasksCache.get(studyApiId, props?.force)
  );
  useEffect(() => {
    resolvedTasks?.reload();
  }, [context.filterIntegrity]);

  const taskPositions = useMemo(() => {
    if (resolvedTasks.state.status === 'fulfilled') {
      return packTasks(
        resolvedTasks.state.value ?? [],
        DateUtils.renderDateTime(context.props.dateRange.start, {
          format: DateRenderFormat.ISODate,
          timezone: context.userTimezone,
        }),
        context.userTimezone
      );
    }
    return new Map<string, TaskPosition>();
  }, [resolvedTasks, context.props.dateRange.start, context.userTimezone]);
  const taskSwimLaneCount = useMemo(
    () => Array.from(taskPositions.values()).reduce((acc, { row }) => (row > acc ? row : acc), 0) + 1,
    [taskPositions]
  );

  return (
    <div
      className={styles.Study}
      data-study-tasks-row={studyApiId}
      style={{ height: `${taskSwimLaneCount * 60 + 1}px` }}
    >
      <div className={styles.Header}>
        <div className="flex flex-row justify-start items-center f8" style={{ width: '200px' }}>
          {_notNil(color?.color) ? (
            <div
              className="ml2"
              style={{ backgroundColor: color?.color ?? 'white', width: '20px', height: '20px', borderRadius: '4px' }}
            />
          ) : null}

          {resolvedStudyInfo?.state?.status === 'fulfilled' ? (
            <div
              className={cn('flex flex-column ml2')}
              style={{ width: '180px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
            >
              <span className="f7 near-black fw5">{resolvedStudyInfo.state.value.name}</span>
              {_notNil(resolvedStudyInfo.state.value.code) ? (
                <span className="f8">{resolvedStudyInfo.state.value.code}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.Grid}>
        {resolvedTasks.state.status === 'fulfilled'
          ? resolvedTasks.state.value?.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                timezone={context.userTimezone}
                position={taskPositions.get(task.id)}
                color={color}
                onClick={() => {
                  openModal('TASK_DETAILS', {
                    taskId: task.id,
                    onTaskUpdate: () => resolvedTasks.reload({ force: true }),
                    onTaskReschedule: () => resolvedTasks.reload({ force: true }),
                    onTaskExecute: (task: TaskV1) => openWorkflow(task.study.id, task.id),
                  });
                }}
              />
            ))
          : null}
        <div className={styles.StudyRowTest}></div>
      </div>
    </div>
  );
};
